/**
 * Repris tel quel d'alfred-console (SuperAlfred/packages/console/src/lib/push.ts).
 *
 * Abonnement au Web Push, côté console.
 *
 * Trois réserves qui expliquent la forme de ce module :
 *
 *   · sur iOS, tout ceci n'existe **que** depuis la PWA ajoutée à l'écran
 *     d'accueil. Dans l'onglet Safari, `PushManager` est absent et
 *     l'autorisation ne peut même pas être demandée ;
 *   · l'autorisation ne peut être demandée qu'en réponse à un geste — d'où une
 *     fonction appelée depuis un bouton, jamais au chargement ;
 *   · un abonnement est propre à un navigateur *et* à une clé serveur. Changer
 *     la paire VAPID invalide tous les abonnements pris auparavant.
 */

/** Ce qui empêche, le cas échéant, de s'abonner sur cet appareil. */
export type Empechement =
  | 'aucun'
  | 'sans-service-worker'
  | 'sans-push'
  | 'refuse'
  | 'hors-pwa-ios';

/**
 * La clé publique VAPID voyage en base64url ; `applicationServerKey` attend
 * des octets. La conversion est obligatoire, et son absence produit une erreur
 * particulièrement opaque du navigateur.
 */
export function base64UrlVersOctets(base64url: string): Uint8Array<ArrayBuffer> {
  const bourrage = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + bourrage).replace(/-/g, '+').replace(/_/g, '/');
  const brut = atob(base64);
  // Le tampon est créé explicitement : `new Uint8Array(taille)` produit un
  // ArrayBufferLike, que `applicationServerKey` refuse — il exige un
  // ArrayBuffer, qu'un tampon partagé ne saurait être.
  const octets = new Uint8Array(new ArrayBuffer(brut.length));
  for (let i = 0; i < brut.length; i++) octets[i] = brut.charCodeAt(i);
  return octets;
}

/** Sommes-nous lancés depuis l'écran d'accueil plutôt que depuis un onglet ? */
export function estInstallee(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // Safari sur iOS ignore display-mode et expose sa propre marque.
  return (navigator as unknown as { standalone?: boolean }).standalone === true;
}

function estIOS(): boolean {
  // iPadOS se présente comme un Mac ; le tactile le trahit.
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/**
 * Ce qui bloque l'abonnement sur cet appareil, avant même de le tenter.
 * Distinguer les cas permet de dire quoi faire plutôt que « indisponible ».
 */
export function empechement(): Empechement {
  if (!('serviceWorker' in navigator)) return 'sans-service-worker';
  if (!('PushManager' in window)) {
    // Sur iPhone, l'absence de PushManager a une cause connue et réparable :
    // la console n'a pas été ajoutée à l'écran d'accueil.
    return estIOS() && !estInstallee() ? 'hors-pwa-ios' : 'sans-push';
  }
  if (Notification.permission === 'denied') return 'refuse';
  return 'aucun';
}

export interface EtatAbonnement {
  actif: boolean;
  /** L'endpoint courant, utile pour se désabonner côté daemon. */
  endpoint: string | null;
}

export async function lireAbonnement(): Promise<EtatAbonnement> {
  if (empechement() !== 'aucun' && empechement() !== 'refuse') {
    return { actif: false, endpoint: null };
  }
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return { actif: Boolean(sub), endpoint: sub?.endpoint ?? null };
}

/**
 * Un libellé qui permette de reconnaître l'appareil dans les réglages. On ne
 * peut rien deviner depuis une URL de service de push.
 */
function libelleAppareil(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'PC Windows';
  return 'appareil';
}

export interface DependancesPush {
  /** Récupère la clé publique VAPID auprès du daemon. */
  cleServeur(): Promise<string>;
  /** Déclare l'abonnement au daemon. */
  declarer(sub: PushSubscriptionJSON & { label: string }): Promise<void>;
  /** Retire l'abonnement du daemon. */
  retirer(endpoint: string): Promise<void>;
}

/**
 * Demande l'autorisation puis s'abonne. À appeler depuis un geste de
 * l'utilisateur — sans quoi le navigateur refuse la demande.
 */
export async function activer(deps: DependancesPush): Promise<EtatAbonnement> {
  const quoi = empechement();
  if (quoi !== 'aucun') throw new Error(quoi);

  // La clé AVANT l'autorisation, et non l'inverse : iOS ne pose la question
  // qu'une fois. La brûler pour une tentative que le daemon va refuser — jeton
  // absent, Web Push éteint — laisserait l'utilisateur avec une autorisation
  // accordée, aucun abonnement, et aucun moyen de redemander.
  const cle = await deps.cleServeur();

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('refuse');

  // `ready` et non `getRegistration` : l'abonnement exige un service worker
  // actif, pas seulement enregistré.
  const reg = await navigator.serviceWorker.ready;

  const existant = await reg.pushManager.getSubscription();
  if (existant) {
    // Déjà abonné côté navigateur, mais le daemon a pu perdre l'abonnement —
    // base repartie de zéro, purge après une panne. On le redéclare.
    await deps.declarer({ ...existant.toJSON(), label: libelleAppareil() });
    return { actif: true, endpoint: existant.endpoint };
  }

  const sub = await reg.pushManager.subscribe({
    // Obligatoire : un push qui n'aboutit à aucune notification visible fait
    // révoquer l'abonnement par le navigateur.
    userVisibleOnly: true,
    applicationServerKey: base64UrlVersOctets(cle),
  });

  await deps.declarer({ ...sub.toJSON(), label: libelleAppareil() });
  return { actif: true, endpoint: sub.endpoint };
}

export async function desactiver(deps: DependancesPush): Promise<EtatAbonnement> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return { actif: false, endpoint: null };

  const endpoint = sub.endpoint;
  // Le daemon d'abord : si le navigateur se désabonne et que la déclaration
  // échoue, il resterait un abonnement mort en base jusqu'à la prochaine purge.
  await deps.retirer(endpoint);
  await sub.unsubscribe().catch(() => undefined);
  return { actif: false, endpoint: null };
}
